import React, { useState, Dispatch, SetStateAction } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ModuleI, ModuleStatus, ResponseEnum, ResponseI } from "../types";
import { useGetTopics } from "../api/queries";
import { useCreateResponse } from "../api/mutations";
import { useNotification } from "../hooks/useNotification";
import Modal from "../componentLibrary/Modal";
import Input from "../componentLibrary/Input";
import Textarea from "../componentLibrary/Textarea";
import Combobox, { ComboboxOptionI } from "../componentLibrary/Combobox";
import Button from "../componentLibrary/Button";
import Spinner from "../componentLibrary/Spinner";

const blankReflection: ResponseI = {
  title: "",
  type: ResponseEnum.REFLECTION,
  content: {
    body: "",
    module: {} as ModuleI,
  },
};

const displayModules: ModuleStatus[] = [ModuleStatus.Published];

export default function AddReflectionModal({
  isModalOpen,
  setIsModalOpen,
}: {
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  habit_prefilled?: string;
}) {
  const [newReflection, setNewReflection] =
    useState<ResponseI>(blankReflection);
  const [comboVal, setComboVal] = useState<ComboboxOptionI>({
    value: "",
    label: "",
  });
  const { data: modules, isError, isLoading } = useGetTopics();
  const { notify } = useNotification();

  const queryClient = useQueryClient();

  const closeModal = () => {
    setNewReflection(blankReflection);
    setComboVal({
      value: "",
      label: "",
    });
    setIsModalOpen(false);
  };

  const mutation = useCreateResponse({
    onSuccess: async () => {
      notify("success", "Reflection created successfully");
      closeModal();
      await queryClient.invalidateQueries({ queryKey: ["responses"] });
    },
    onError: async () => {
      notify("error", "There was an error creating the reflection.");
    },
  });

  if (isLoading) return <Spinner />;
  if (isError || !modules) return <div>Error</div>;

  return (
    <Modal
      title="Add Reflection"
      size="sm"
      isOpen={isModalOpen}
      onCloseModal={closeModal}
      height="fit-content"
    >
      <div className="h-[440px] flex flex-col justify-between space-y-3">
        <div className="space-y-3">
          <Input
            label="Entry Title"
            value={newReflection.title}
            placeholder={`${new Date().toLocaleString("en-US", { weekday: "long" })} - ${new Date().toLocaleDateString()}`}
            convertPlaceholder
            onChange={(e) =>
              setNewReflection((prev) => ({
                ...prev,
                title: e.target.value,
              }))
            }
          />
          <Textarea
            value={newReflection.content.body}
            placeholder="Today I noticed..."
            rows={5}
            onChange={(e) =>
              setNewReflection((prev) => ({
                ...prev,
                content: {
                  ...prev.content,
                  body: e.target.value,
                },
              }))
            }
          />
          <Combobox
            label="Does it connect to a module?"
            options={modules
              .filter((mod) => displayModules.includes(mod.status))
              .map((mod) => ({
                label: mod.title,
                value: mod._id,
              }))}
            value={comboVal}
            onChange={(option) => {
              const selectedModule = modules.find(
                (m) => m._id === option.value,
              );
              if (selectedModule)
                setNewReflection((prev) => ({
                  ...prev,
                  content: {
                    ...prev.content,
                    module: selectedModule,
                  },
                }));
              setComboVal(option);
            }}
          />
        </div>
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={() => mutation.mutate(newReflection)}
        >
          Submit
        </Button>
      </div>
    </Modal>
  );
}
